import {
  createFileRoute,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { MeetingsList } from '@/components/MeetingsList';
import { TranscriptUpload } from '@/components/TranscriptUpload';
import { CreateMeetingModal } from '@/components/CreateMeetingModal';
import { useState, useCallback } from 'react';
import {
  IressContainer,
  IressStack,
  IressText,
  IressButton,
  IressModal,
  IressAlert,
} from '@iress-oss/ids-components';
import { uploadTranscript, createMeeting } from '@/lib/api';
import { meetingQueryOptions } from '@/lib/query/meetingQuery';
import { meetingsQueryOptions } from '@/lib/query/meetingsQuery';

interface HomeSearch {
  finalise?: string;
}

export const Route = createFileRoute('/')({
  validateSearch: (search: Record<string, unknown>): HomeSearch => ({
    finalise: typeof search.finalise === 'string' ? search.finalise : undefined,
  }),
  component: HomePage,
});

function HomePage() {
  const [showNextError, setShowNextError] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { finalise: finaliseMeetingId } = useRouterState({
    select: (s) => s.location.search,
  }) as HomeSearch;

  const handleFileChange = useCallback((file: File | null) => {
    setSelectedFile(file);
    if (file !== null) {
      setShowNextError(false);
      setUploadError(null);
    }
  }, []);

  const handleModalClose = useCallback(() => {
    setSelectedFile(null);
    setShowNextError(false);
    setUploadError(null);
    void navigate({ to: '/', search: {} });
  }, [navigate]);

  const handleNextClick = useCallback(async () => {
    const text = await selectedFile?.text();
    if (!text) {
      setShowNextError(true);
      return;
    }
    setIsUploading(true);
    try {
      const updated = await uploadTranscript(finaliseMeetingId!, text);
      queryClient.setQueryData(
        meetingQueryOptions(finaliseMeetingId!).queryKey,
        updated,
      );
      void queryClient.invalidateQueries({
        queryKey: meetingsQueryOptions.queryKey,
      });
      void navigate({
        to: '/$id/summarise',
        params: { id: finaliseMeetingId! },
      });
    } catch {
      setUploadError('Failed to upload transcript. Please try again.');
    } finally {
      setIsUploading(false);
    }
  }, [navigate, finaliseMeetingId, selectedFile, queryClient]);

  return (
    <IressContainer py="lg">
      <IressStack gap="spacing.8">
        <header>
          <IressStack gap="spacing.2">
            <IressText element="h1" textStyle="typography.heading.1">
              Xplan Meeting Assistant
            </IressText>
            <IressText textStyle="typography.body.md">
              AI powered assistant for Xplan Workflows - Meeting Preparation and
              Management
            </IressText>
          </IressStack>
        </header>

        <MeetingsList
          onFinalise={(meeting) => {
            if (meeting.transcript) {
              void navigate({
                to: '/$id/summarise',
                params: { id: meeting.id },
              });
            } else {
              void navigate({
                to: '/',
                search: { finalise: meeting.id },
              });
            }
          }}
          onCreate={() => setShowCreateModal(true)}
        />

        <CreateMeetingModal
          show={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreate={async (data) => {
            try {
              const created = await createMeeting(data);
              setShowCreateModal(false);
              void queryClient.invalidateQueries({
                queryKey: meetingsQueryOptions.queryKey,
              });
              void navigate({ to: '/', search: { finalise: created.id } });
            } catch {
              setUploadError('Failed to create meeting. Please try again.');
            }
          }}
        />

        <IressModal
          show={!!finaliseMeetingId}
          onShowChange={(show) => {
            if (!show) handleModalClose();
          }}
          heading="Upload existing transcript/meeting recording"
          size="lg"
          data-testid="finalise-modal"
          footer={
            <IressButton
              mode="primary"
              onClick={handleNextClick}
              loading={isUploading ? 'Uploading...' : undefined}
            >
              Next
            </IressButton>
          }
          disableBackdropClick={isUploading}
          noCloseButton={isUploading}
        >
          <IressStack gap="spacing.4">
            <TranscriptUpload
              hideHeading
              file={selectedFile}
              onFileChange={handleFileChange}
            />
            {showNextError && (
              <IressAlert status="warning">
                Please upload a transcript before proceeding.
              </IressAlert>
            )}
            {uploadError && (
              <IressAlert status="danger">{uploadError}</IressAlert>
            )}
          </IressStack>
        </IressModal>
      </IressStack>
    </IressContainer>
  );
}
